/**
 * X & O Breakdowns — full-screen page rendered when nav === "xo".
 *
 * Three layers stacked:
 *   1. Page header with the section title + tagline
 *   2. Compact "Our Offensive Vocab" link card — navigates to the
 *      standalone vocab module (#/vocab). Lives inline so the vocab
 *      is one click away from any X&O entry.
 *   3. Content browser: top-level category nav (All / Concepts /
 *      Trigger Library / Defense / Sets / Single Play Breakdowns),
 *      Concepts-only Type sub-filter that appears when Concepts is
 *      selected, and a tag chip row across all categories.
 *
 * Drills come from useDrills('xo') — real DB query. Empty state when
 * no entries match the active filters; nothing seeded.
 */
import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { BookOpen, Sparkles, Lock, Plus, Play, X, Flame, Clipboard } from "lucide-react";
import { GOLD } from "../constants/theme";
import { useDrills } from "../hooks/useDrills";
import BamLoading from "../shared/BamLoading";
import { useBamIQPage } from "../bamiq/BamIQ";

const MuxPlayer = lazy(() => import("@mux/mux-player-react"));

// ── X & O taxonomy ───────────────────────────────────────────────────

const PLAY_CATEGORIES = [
  { id: "all",        label: "All" },
  { id: "concepts",   label: "Concepts" },
  { id: "triggers",   label: "Trigger Library", comingSoon: true },
  { id: "defense",    label: "Defense" },
  { id: "sets",       label: "Sets" },
  { id: "breakdowns", label: "Single Play Breakdowns" },
];

const CONCEPT_SUBS = [
  { id: "all",        label: "All Types" },
  { id: "tips",       label: "Tips & Tricks", tooltip: "Little things to teach your players tactically" },
  { id: "counters",   label: "Counters" },
  { id: "principles", label: "Principles" },
  { id: "tools",      label: "Tools" },
];

const CONCEPT_TAGS = [
  { id: "advantage creation", label: "Advantage Creation" },
  { id: "spacing",            label: "Spacing" },
  { id: "ball screens",       label: "Ball Screens" },
  { id: "transition",         label: "Transition" },
  { id: "off-ball",           label: "Off-Ball" },
  { id: "post play",          label: "Post Play" },
];

// ── Page component ───────────────────────────────────────────────────

export function XOPage({ C, dark, newSince = null }) {
  const [cat, setCat] = useState("all");
  const [sub, setSub] = useState("all");
  const [tag, setTag] = useState("all");
  const [tipFor, setTipFor] = useState(null);
  // Drill currently open in the lightbox player. null = no overlay.
  const [playing, setPlaying] = useState(null);
  // The breakdown that's playing, offered to BAM IQ for the coach to attach (D122).
  useBamIQPage(playing?.id ? { type: "drill", id: playing.id, title: playing.title } : null);

  const { items, loading } = useDrills("xo");

  // A plain X&O drill deep link (bookmark, embed) lands here and plays.
  useEffect(() => {
    if (loading) return;
    let target = null;
    try { const b = JSON.parse(sessionStorage.getItem("bam_open_drill") || "null"); if (b?.sectionId === "xo" && b.id) target = b; } catch {}
    if (!target) return;
    try { sessionStorage.removeItem("bam_open_drill"); } catch {}
    const d = items.find(x => String(x.id) === String(target.id));
    if (d) setPlaying(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items]);

  const inConcepts = cat === "concepts";
  const isComingSoon = cat === "triggers";

  // "New" = added since this coach last opened X & O, floored at 14 days
  // so nothing stays new forever. A coach with no previous visit gets the
  // last 7 days instead, so the page still points at what's fresh.
  const newCutoff = useMemo(() => {
    const floor = Date.now() - 14 * 864e5;
    if (newSince) { const t = new Date(newSince).getTime(); return Number.isNaN(t) ? floor : Math.max(t, floor); }
    return Date.now() - 7 * 864e5;
  }, [newSince]);
  const isNew = (d) => { const t = d.createdAt ? new Date(d.createdAt).getTime() : NaN; return !Number.isNaN(t) && t > newCutoff; };
  const newSet = useMemo(() => new Set((Array.isArray(items) ? items : []).filter(isNew).map(d => d.id)), [items, newCutoff]); // eslint-disable-line react-hooks/exhaustive-deps
  const newByCat = useMemo(() => {
    const m = {};
    for (const d of (Array.isArray(items) ? items : [])) if (newSet.has(d.id) && d.xoCategory) m[d.xoCategory] = (m[d.xoCategory] || 0) + 1;
    return m;
  }, [items, newSet]);

  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const list = items.filter(d => {
      if (cat !== "all" && d.xoCategory !== cat) return false;
      if (inConcepts && sub !== "all" && d.xoSubcategory !== sub) return false;
      if (tag !== "all" && !(d.tags || []).some(t => t.toLowerCase() === tag)) return false;
      return true;
    });
    // New ones lead the grid; the rest keep their order.
    return [...list.filter(d => newSet.has(d.id)), ...list.filter(d => !newSet.has(d.id))];
  }, [items, cat, sub, tag, inConcepts, newSet]);
  const newShown = filtered.filter(d => newSet.has(d.id)).length;

  const switchCat = (id) => { setCat(id); setSub("all"); /* tag stays — works across categories */ };

  return (
    <div className="bam-content-scroll" style={{ flex: 1, overflow: "auto", padding: 28, background: C.bg, color: C.text }}>
      {/* Page-scoped keyframes for the cinematic play-button breathe.
          The spinner inside PlayerOverlay relies on the global `spin`
          keyframes from dashboard.css. */}
      <style>{`
        @keyframes xoPlayPulse {
          0%, 100% {
            box-shadow: 0 0 0 6px rgba(226,221,159,0.10), 0 0 28px rgba(226,221,159,0.20), 0 8px 24px rgba(0,0,0,0.5);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 0 11px rgba(226,221,159,0.04), 0 0 38px rgba(226,221,159,0.34), 0 8px 24px rgba(0,0,0,0.5);
            transform: scale(1.05);
          }
        }
      `}</style>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header — same block as every content section (icon + title,
            one-line tagline), hidden on phones where the topbar already
            names the section. The vocab and set-of-the-day links sit as
            small chips on the right instead of a banner. */}
        <div className="bam-content-header" style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <Clipboard size={20} color={GOLD} strokeWidth={2}/>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: 0.5 }}>X &amp; O Breakdowns</div>
              </div>
              <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.5 }}>Breaking down the tactical side of the game in-depth.</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <LinkChip C={C} icon={Flame} label="Set of the day" onClick={() => { window.location.hash = "#/playbook"; }}/>
              <VocabChip C={C}/>
            </div>
          </div>
        </div>

        {/* Category tabs — same round pills as the drills page. A tab
            carries a gold count when something landed in it since this
            coach last opened X & O, the way the side nav does. */}
        <div className="bam-chip-row" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          {PLAY_CATEGORIES.map(c => {
            const on = cat === c.id;
            const n = c.id === "all" ? 0 : (newByCat[c.id] || 0);
            return (
              <button key={c.id} onClick={() => switchCat(c.id)} type="button"
                style={{ fontSize: 12, fontWeight: 700, padding: "6px 16px", borderRadius: 20, cursor: "pointer",
                  border: `1px solid ${on ? GOLD : C.border}`,
                  background: on ? GOLD : "transparent",
                  color: on ? "#111" : (c.comingSoon ? C.textDim : C.textMid),
                  fontFamily: "inherit", whiteSpace: "nowrap",
                  display: "inline-flex", alignItems: "center", gap: 7,
                  transition: "all .15s",
                }}>
                {c.label}
                {c.comingSoon && (
                  <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: on ? "rgba(0,0,0,0.12)" : `${GOLD}22`, color: on ? "#111" : GOLD, letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 800 }}>Soon</span>
                )}
                {n > 0 && !on && (
                  <span title={`${n} added since you last looked`}
                    style={{ fontSize: 8.5, fontWeight: 800, color: "#0A0A0A", background: GOLD, minWidth: 16, height: 16, borderRadius: 8, padding: "0 5px",
                      display: "inline-flex", alignItems: "center", justifyContent: "center", letterSpacing: 0.3 }}>{n > 9 ? "9+" : n}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filter row — matches ContentPage filter pattern exactly:
            "All" pill, then "TYPE:" / "TAG:" group labels with their
            chips inline. Pill chips use solid GOLD fill when active
            (different from tabs which use the tint). */}
        {!isComingSoon && (
          <div style={{ marginBottom: 22 }}>
            <div className="bam-chip-row" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {/* "All" master pill — only meaningful when tag filter is on */}
              <PillFilter C={C} active={tag === "all" && (!inConcepts || sub === "all")}
                onClick={() => { setTag("all"); setSub("all"); }}>All</PillFilter>

              {/* Type group (only when Concepts is the active category) */}
              {inConcepts && (
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginRight: 2 }}>Type:</span>
                  {CONCEPT_SUBS.filter(s => s.id !== "all").map(s => (
                    <span key={s.id} style={{ position: "relative" }}
                      onMouseEnter={() => s.tooltip && setTipFor(s.id)}
                      onMouseLeave={() => setTipFor(null)}>
                      <PillFilter C={C} active={sub === s.id} onClick={() => setSub(s.id)}>{s.label}</PillFilter>
                      {tipFor === s.id && s.tooltip && (
                        <span style={{
                          position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
                          padding: "6px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600,
                          background: "#0a0a0a", color: C.text, border: `1px solid ${C.border}`,
                          whiteSpace: "nowrap", maxWidth: 280, lineHeight: 1.3, zIndex: 5,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.5)",
                        }}>{s.tooltip}</span>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {/* Tag group */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginRight: 2 }}>Tag:</span>
                {CONCEPT_TAGS.map(tg => (
                  <PillFilter key={tg.id} C={C} active={tag === tg.id} onClick={() => setTag(tg.id)}>{tg.label}</PillFilter>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        {isComingSoon ? (
          <div style={{
            padding: "60px 30px", textAlign: "center", borderRadius: 14,
            background: `${GOLD}06`, border: `1px dashed ${GOLD}55`,
          }}>
            <Sparkles size={36} color={GOLD} style={{ opacity: 0.85, marginBottom: 14 }}/>
 <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: GOLD, letterSpacing: 1.6, textTransform: "uppercase", marginBottom: 8 }}>Trigger Library: Coming Soon</div>
            <div style={{ fontSize: 13, color: C.textMid, maxWidth: 460, margin: "0 auto", lineHeight: 1.55 }}>
 A library of quick advantage-creation triggers and structured 3-man starts: Ball Screen, Ghost, Flare, Get, Pitch, Ram, Veer, and more. Filterable by tag, indexed by the BAM/ADAPT vocabulary.
            </div>
          </div>
        ) : loading ? (
          <BamLoading pad={60}/>
        ) : filtered.length === 0 ? (
          <EmptyState C={C} cat={cat} sub={sub} tag={tag} hasAny={items.length > 0}/>
        ) : (
          <>
            {newShown > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
                <span style={{ fontSize: 16 }}>⚡</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: .5 }}>
                  {newSince ? "New since your last visit" : "New this week"}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.textDim }}>({newShown})</span>
                <div style={{ flex: 1, height: 1, background: C.border }}/>
              </div>
            )}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 14,
            }}>
              {filtered.map((d, i) => (
                <React.Fragment key={d.id}>
                  {newShown > 0 && i === newShown && (
                    <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 9, margin: "12px 0 0" }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: .5 }}>Everything else</span>
                      <div style={{ flex: 1, height: 1, background: C.border }}/>
                    </div>
                  )}
                  <PlayCard drill={d} C={C} onPlay={setPlaying} isNew={newSet.has(d.id)}/>
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>
      <PlayerOverlay drill={playing} C={C} onClose={() => setPlaying(null)}/>
    </div>
  );
}

// ── Small header link chip (set of the day) ──────────────────────────
function LinkChip({ C, icon: Icon, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick} type="button"
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "8px 13px", borderRadius: 8,
        background: hover ? `${GOLD}18` : `${GOLD}0c`,
        color: hover ? GOLD : C.text,
        border: `1px solid ${hover ? `${GOLD}66` : `${GOLD}33`}`,
        fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
        fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap",
        transition: "background .15s, color .15s, border-color .15s",
      }}>
      <Icon size={13} color={GOLD} style={{ opacity: hover ? 1 : 0.85 }}/>
      {label}
      <span style={{ marginLeft: 2, fontSize: 11, color: hover ? GOLD : C.textDim }}>→</span>
    </button>
  );
}

// ── Vocab chip ───────────────────────────────────────────────────────
// Tiny header pill that sits to the right of the page title. Single-
// line, no wallpaper, just an icon + label + arrow. Hover surfaces a
// brief explainer tooltip. Click navigates to the standalone vocab
// module at #/vocab.

function VocabChip({ C }) {
  const [hover, setHover] = useState(false);
  return (
    <span style={{ position: "relative", flexShrink: 0 }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button onClick={() => { window.location.hash = "#/vocab"; }} type="button"
        style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          padding: "8px 13px", borderRadius: 8,
          background: hover ? `${GOLD}18` : `${GOLD}0c`,
          color: hover ? GOLD : C.text,
          border: `1px solid ${hover ? `${GOLD}66` : `${GOLD}33`}`,
          fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
          fontFamily: "inherit", cursor: "pointer",
          transition: "background .15s, color .15s, border-color .15s",
          whiteSpace: "nowrap",
        }}>
        <BookOpen size={13} color={hover ? GOLD : GOLD} style={{ opacity: hover ? 1 : 0.85 }}/>
        Our Offensive Vocab
        <span style={{ marginLeft: 2, fontSize: 11, color: hover ? GOLD : C.textDim }}>→</span>
      </button>
      {hover && (
        <span style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: 240,
          padding: "10px 14px 12px",
          borderRadius: 8,
          fontSize: 11.5, lineHeight: 1.45,
          background: "rgba(10,10,10,0.92)",
          color: C.text,
          border: `1px solid ${GOLD}44`,
          borderTop: `2px solid ${GOLD}`,
          backdropFilter: "blur(8px) saturate(1.2)",
          WebkitBackdropFilter: "blur(8px) saturate(1.2)",
          boxShadow: `0 12px 32px rgba(0,0,0,0.6), 0 0 18px ${GOLD}22`,
          zIndex: 5,
          textAlign: "left",
        }}>
          {/* Caret pointing back to the chip */}
          <span style={{
            position: "absolute", top: -7, right: 22,
            width: 12, height: 12,
            background: "rgba(10,10,10,0.92)",
            borderTop: `2px solid ${GOLD}`,
            borderLeft: `1px solid ${GOLD}44`,
            transform: "rotate(45deg)",
            transformOrigin: "center",
          }}/>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 10, letterSpacing: 1.6, color: GOLD, textTransform: "uppercase", marginBottom: 4, fontWeight: 600 }}>
            BAM / ADAPT Glossary
          </div>
          <div style={{ color: C.text, fontWeight: 500 }}>
            The shared terminology that powers every breakdown below.
          </div>
        </span>
      )}
    </span>
  );
}

// ── Empty state ──────────────────────────────────────────────────────

function EmptyState({ C, cat, sub, tag, hasAny }) {
  const filtered = cat !== "all" || sub !== "all" || tag !== "all";
  return (
    <div style={{ padding: "80px 30px", textAlign: "center", borderRadius: 14, border: `1px dashed ${C.border}`, background: "rgba(255,255,255,0.015)" }}>
      <Plus size={28} color={C.textDim} style={{ marginBottom: 14 }}/>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: C.text, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 8 }}>
        {hasAny && filtered ? "No entries match" : "Nothing here yet"}
      </div>
      <div style={{ fontSize: 13, color: C.textMid, maxWidth: 460, margin: "0 auto", lineHeight: 1.55 }}>
        {hasAny && filtered
          ? "Try a different filter, or clear the filters to see everything."
          : "X & O breakdowns will land here as they get uploaded. Check back soon."}
      </div>
    </div>
  );
}

// ── Cards & chips ────────────────────────────────────────────────────

function PlayCard({ drill, C, onPlay, isNew = false }) {
  const [hover, setHover] = useState(false);
  const subLabel = drill.xoSubcategory
    ? CONCEPT_SUBS.find(s => s.id === drill.xoSubcategory)?.label
    : drill.xoCategory
      ? PLAY_CATEGORIES.find(c => c.id === drill.xoCategory)?.label
      : (drill.category || "X & O");

  // Wider Mux thumbnail since the cinematic 16:9 layout has more horizontal
  // pixels than the previous 16:10 framing.
  const thumb = drill.muxPlaybackId
    ? `https://image.mux.com/${drill.muxPlaybackId}/thumbnail.jpg?width=720`
    : null;
  const playable = !!drill.muxPlaybackId;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => { if (playable) onPlay?.(drill); }}
      role={playable ? "button" : undefined}
      tabIndex={playable ? 0 : -1}
      onKeyDown={(e) => { if (playable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onPlay?.(drill); } }}
 aria-label={playable ? `Play ${drill.title}` : `${drill.title}: coming soon`}
      style={{
        position: "relative", aspectRatio: "16/9",
        background: thumb ? "#000" : `linear-gradient(135deg, ${GOLD}22 0%, #0A0A0A 70%)`,
        borderRadius: 8, overflow: "hidden", cursor: playable ? "pointer" : "not-allowed",
        border: `1px solid ${hover && playable ? `${GOLD}` : C.border}`,
        transition: "border-color .25s ease, box-shadow .35s ease",
        boxShadow: hover && playable
          ? `0 0 0 1px ${GOLD}55, 0 18px 38px rgba(0,0,0,0.55), 0 0 28px ${GOLD}22`
          : "0 4px 12px rgba(0,0,0,0.4)",
      }}>
      {/* Thumbnail — slow zoom on hover, slight desaturation off-state so
          the gold accents read first when you're scanning the grid. */}
      {thumb && (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover",
            opacity: hover ? 0.95 : 0.82,
            transform: hover ? "scale(1.045)" : "scale(1)",
            filter: hover ? "saturate(1.1)" : "saturate(0.85)",
            transition: "transform .9s cubic-bezier(.2,.8,.2,1), opacity .35s ease, filter .35s ease",
          }}
        />
      )}

      {/* Subtle X / O watermark layer — just enough to whisper "tactical
          film" without competing with the thumbnail content. */}
      <svg
        viewBox="0 0 400 225"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          opacity: hover && playable ? 0.16 : 0.08,
          transition: "opacity .35s ease",
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      >
        <text x="20"  y="60"  fontFamily="'Bebas Neue', sans-serif" fontSize="48" fontWeight="900" fill={GOLD}>X</text>
        <text x="78"  y="195" fontFamily="'Bebas Neue', sans-serif" fontSize="40" fontWeight="900" fill="#fff">O</text>
        <text x="316" y="46"  fontFamily="'Bebas Neue', sans-serif" fontSize="34" fontWeight="900" fill="#fff">X</text>
        <text x="350" y="208" fontFamily="'Bebas Neue', sans-serif" fontSize="44" fontWeight="900" fill={GOLD}>O</text>
      </svg>

      {/* Court line that draws across on hover — the moving thing the eye
          locks onto when you mouse over a card. stroke-dashoffset goes
          from full length to 0 over 0.9s. */}
      <svg
        viewBox="0 0 400 225"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      >
        <path
          d="M -20 120 Q 140 40 250 110 T 420 130"
          stroke={GOLD}
          strokeWidth="1.4"
          fill="none"
          strokeDasharray="700"
          strokeDashoffset={hover && playable ? 0 : 700}
          style={{
            transition: "stroke-dashoffset 0.95s cubic-bezier(.4,0,.2,1)",
            opacity: 0.55,
            filter: `drop-shadow(0 0 4px ${GOLD}88)`,
          }}
        />
      </svg>

      {/* Cinematic vignette + bottom gradient. Stronger than before so the
          title reads at any thumbnail brightness. */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.45) 100%)", pointerEvents: "none" }}/>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.94) 100%)", pointerEvents: "none" }}/>

      {/* Top-left category — minimal pill with a gold tick mark. Bebas
          Neue keeps it editorial, not stickery. */}
      <div style={{ position: "absolute", top: 11, left: 12, display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{ width: 14, height: 1.5, background: GOLD }}/>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 12, color: GOLD, letterSpacing: 1.4, fontWeight: 600, textTransform: "uppercase" }}>
          {subLabel}
        </div>
        {isNew && (
          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.8, color: "#0A0A0A", background: GOLD,
            padding: "2px 6px", borderRadius: 3, textTransform: "uppercase", lineHeight: 1.3 }}>New</span>
        )}
      </div>

      {/* Duration in monospace — feels like a film stripe timecode. */}
      {drill.duration && drill.duration !== "0:00" && (
        <div style={{
          position: "absolute", top: 11, right: 12,
          fontSize: 10, fontWeight: 600,
          color: "rgba(255,255,255,0.78)",
          fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          letterSpacing: 0.6,
        }}>
          {drill.duration}
        </div>
      )}

      {/* Title + tags. Title in Bebas Neue, condensed and uppercase, scales
          slightly down at long titles via line-clamp. */}
      <div style={{ position: "absolute", bottom: 12, left: 14, right: 14 }}>
        <div style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 20, fontWeight: 600,
          color: "#fff", letterSpacing: 0.7, lineHeight: 1.04,
          textTransform: "uppercase",
          marginBottom: 4,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          textShadow: "0 2px 14px rgba(0,0,0,0.6)",
        }}>
          {drill.title}
        </div>
        {(drill.tags || []).length > 0 && (
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", letterSpacing: 0.4 }}>
            {drill.tags.slice(0, 3).join(" · ")}
          </div>
        )}
      </div>

      {/* Glassmorphic play button — gold ring with a soft halo + breathing
          animation. Triangle is offset 2px right for optical centering. */}
      {hover && playable && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "rgba(10,10,10,0.45)",
            backdropFilter: "blur(10px) saturate(1.2)",
            WebkitBackdropFilter: "blur(10px) saturate(1.2)",
            border: `1.5px solid ${GOLD}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 0 0 6px ${GOLD}1A, 0 0 28px ${GOLD}33, 0 8px 24px rgba(0,0,0,0.5)`,
            animation: "xoPlayPulse 2.4s ease-in-out infinite",
          }}>
            <Play size={22} color={GOLD} fill={GOLD} style={{ marginLeft: 3 }}/>
          </div>
        </div>
      )}

      {/* Coming Soon for genuine placeholders (no Mux ID). */}
      {!playable && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6 }}>
          <Lock size={18} color={GOLD}/>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 12, fontWeight: 600, color: "#fff", letterSpacing: 1.4, textTransform: "uppercase" }}>Coming Soon</div>
        </div>
      )}
    </div>
  );
}

// Lightweight full-screen player overlay for X & O cards. Reuses Mux
// Player so we don't pull in the full ContentDetail panel for what is
// fundamentally just "watch this clip". Click backdrop or press Esc to
// close.
function PlayerOverlay({ drill, C, onClose }) {
  if (!drill) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={drill.title}
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
      className="bam-xo-overlay"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} className="bam-xo-overlay-inner" style={{ width: "100%", maxWidth: 1100, position: "relative" }}>
        <button
          onClick={onClose}
          aria-label="Close player"
          className="bam-xo-overlay-close"
          style={{
            position: "absolute", top: -36, right: 0,
            width: 32, height: 32, borderRadius: 16,
            background: "rgba(255,255,255,0.1)", border: `1px solid ${GOLD}55`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", zIndex: 2,
          }}
        >
          <X size={16} color="#fff"/>
        </button>
        <div style={{ borderRadius: 12, overflow: "hidden", background: "#000", aspectRatio: "16/9" }}>
          <Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}><div style={{ width: 32, height: 32, border: `3px solid ${GOLD}33`, borderTop: `3px solid ${GOLD}`, borderRadius: "50%", animation: "spin 1s linear infinite" }}/></div>}>
            <MuxPlayer
              playbackId={drill.muxPlaybackId}
              streamType="on-demand"
              autoPlay
              accentColor={GOLD}
              metadata={{ video_title: drill.title }}
              style={{ width: "100%", height: "100%" }}
            />
          </Suspense>
        </div>
        <div style={{ marginTop: 12, color: "#fff" }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{drill.title}</div>
          {drill.desc && <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.5, marginTop: 4 }}>{drill.desc}</div>}
        </div>
      </div>
    </div>
  );
}

// Pill filter chip — matches the ContentPage filter pattern exactly:
// fontSize 12, fontWeight 700, padding 6/16, borderRadius 20.
// Active = solid GOLD fill with #111 text. Inactive = transparent
// with C.border outline + textMid copy.
function PillFilter({ C, active, onClick, children }) {
  return (
    <button onClick={onClick} type="button"
      style={{
        fontSize: 12, fontWeight: 700, padding: "6px 16px", borderRadius: 20,
        border: `1px solid ${active ? GOLD : C.border}`,
        background: active ? GOLD : "transparent",
        color: active ? "#111" : C.textMid,
        cursor: "pointer", whiteSpace: "nowrap",
        fontFamily: "inherit", transition: "all .15s",
      }}>{children}</button>
  );
}

export default XOPage;
